import { useMemo } from 'react';

import {
  type FrozenRate,
  type StoredAmounts,
  convertAmount,
  isSameCurrency,
} from '../services/expense/ConversionService';

export interface UseExpenseConversionInput {
  groupCurrency: string;
  enteredCurrency: string;
  /** What the user typed, in `enteredCurrency`. */
  enteredAmount: number;
  /** From the app's query hook. */
  preview: { rate: number; rateDate: string } | null;
  /** User override. */
  manualRate: number | null;
  /** No network: a foreign entry without a manual rate queues unconverted. */
  offline: boolean;
  /** Edit mode: stored on the expense until an input changes. */
  frozen: FrozenRate | null;
  /** Edit mode: the loaded expense's amounts, kept while `frozen` applies. */
  storedAmounts?: StoredAmounts | null;
}

export interface ConversionState {
  isForeign: boolean;
  /** Group-currency amount for splits/payers. */
  numericAmount: number;
  effectiveRate: number | null;
  rateDate: string | null;
  rateSource: 'provider' | 'manual' | null;
  /** `!isForeign || effectiveRate !== null`. */
  canSubmit: boolean;
  /** `isForeign && effectiveRate === null && !offline`. */
  needsRate: boolean;
  /** `offline && isForeign && manualRate === null` — queue in the original currency. */
  pendingConversion: boolean;
}

export function useExpenseConversion(
  i: UseExpenseConversionInput
): ConversionState {
  return useMemo(() => {
    const isForeign = !isSameCurrency(i.enteredCurrency, i.groupCurrency);
    if (!isForeign) {
      return {
        isForeign,
        numericAmount: i.enteredAmount,
        effectiveRate: null,
        rateDate: null,
        rateSource: null,
        canSubmit: true,
        needsRate: false,
        pendingConversion: false,
      };
    }

    const pick =
      i.manualRate !== null
        ? { rate: i.manualRate, rateDate: null, source: 'manual' as const }
        : i.frozen
          ? {
              rate: i.frozen.rate,
              rateDate: i.frozen.rateDate,
              source: i.frozen.source,
            }
          : i.preview
            ? {
                rate: i.preview.rate,
                rateDate: i.preview.rateDate,
                source: 'provider' as const,
              }
            : null;

    if (!pick) {
      if (i.offline) {
        return {
          isForeign,
          numericAmount: i.enteredAmount,
          effectiveRate: null,
          rateDate: null,
          rateSource: null,
          canSubmit: true,
          needsRate: false,
          pendingConversion: true,
        };
      }
      return {
        isForeign,
        numericAmount: 0,
        effectiveRate: null,
        rateDate: null,
        rateSource: null,
        canSubmit: false,
        needsRate: true,
        pendingConversion: false,
      };
    }

    // Rows from older clients can store an amount one minor unit off
    // `round(original × rate)`; recomputing it would move the total (or
    // fail the payer check) on an edit that never touched the amount.
    const stored = i.manualRate === null && i.frozen ? i.storedAmounts : null;

    return {
      isForeign,
      numericAmount:
        stored && i.enteredAmount === stored.originalAmount
          ? stored.amount
          : convertAmount(i.enteredAmount, pick.rate, i.groupCurrency),
      effectiveRate: pick.rate,
      rateDate: pick.rateDate,
      rateSource: pick.source,
      canSubmit: true,
      needsRate: false,
      pendingConversion: false,
    };
    // Depend on the primitive fields of `preview`/`frozen` rather than the
    // objects themselves — the caller's query hook and edit-mode state
    // recreate those objects on every render even when their values haven't
    // changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    i.groupCurrency,
    i.enteredCurrency,
    i.enteredAmount,
    i.preview?.rate,
    i.preview?.rateDate,
    i.manualRate,
    i.offline,
    i.frozen?.rate,
    i.frozen?.rateDate,
    i.frozen?.source,
    i.storedAmounts?.amount,
    i.storedAmounts?.originalAmount,
  ]);
}
